import { Badge, Card, Group, SimpleGrid, Stack, Text, ThemeIcon, Title, Tooltip } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import type { ReactNode } from "react";

import { useT } from "../i18n/script";

export const fmtNum = (v: unknown, d = 2): string => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("sr-Latn-BA", { maximumFractionDigits: d, minimumFractionDigits: 0 });
};
export const fmtDate = (v: unknown): string => {
  if (!v) return "—";
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString("sr-Latn-BA");
};
export const fmtDateTime = (v: unknown): string => (v ? new Date(String(v)).toLocaleString("sr-Latn-BA") : "—");

export function PageHeader({ title, description, right }: { title: string; description?: string; right?: ReactNode }) {
  const { t } = useT();
  return (
    <Group justify="space-between" align="flex-start" mb="md" wrap="wrap">
      <Group gap={6} align="center">
        <Title order={3}>{t(title)}</Title>
        {description && (
          <Tooltip label={t(description)} multiline w={360} withArrow position="bottom-start" openDelay={150}>
            <ThemeIcon variant="subtle" color="gray" size="sm" radius="xl" style={{ cursor: "help" }} aria-label={t(description)}>
              <IconInfoCircle size={16} />
            </ThemeIcon>
          </Tooltip>
        )}
      </Group>
      {right && <Group gap="xs">{right}</Group>}
    </Group>
  );
}

export interface Kpi { label: string; value: ReactNode; unit?: string; icon?: ReactNode; color?: string; hint?: ReactNode; onClick?: () => void }

export function KpiGrid({ items, cols = 4 }: { items: Kpi[]; cols?: number }) {
  const { t } = useT();
  return (
    <SimpleGrid cols={{ base: 1, xs: 2, md: cols }} spacing="sm">
      {items.map((k) => (
        <Card key={k.label} withBorder padding="md" onClick={k.onClick} style={{ cursor: k.onClick ? "pointer" : undefined }}>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>{t(k.label)}</Text>
              <Text fz={24} fw={700} lh={1.2}>{k.value}{k.unit && <Text span size="sm" c="dimmed" fw={500}> {k.unit}</Text>}</Text>
              {k.hint && <Text size="xs" c="dimmed">{k.hint}</Text>}
            </Stack>
            {k.icon && <ThemeIcon size={38} radius="md" variant="light" color={k.color ?? "agri"}>{k.icon}</ThemeIcon>}
          </Group>
        </Card>
      ))}
    </SimpleGrid>
  );
}

const STATUS: Record<string, [string, string]> = {
  zavrseno: ["green", "Završeno"], nepotpuno: ["orange", "Nepotpuno"], kasni: ["red", "Kasni"], u_toku: ["blue", "U toku"],
  predstoji: ["gray", "Predstoji"], propusteno: ["gray", "Propušteno"], aktivan: ["green", "Aktivan"], storniran: ["red", "Storniran"],
  u_skladu: ["green", "U skladu"], nepravilnost: ["red", "Nepravilnost"], done: ["green", "Završeno"], failed: ["red", "Greška"],
  running: ["blue", "U toku"], queued: ["gray", "Na čekanju"], u_skladistu: ["blue", "U skladištu"], arhiviran: ["grape", "Arhiviran"],
  u_radu: ["blue", "U radu"], aktivno: ["green", "Aktivno"], u_podizanju: ["yellow", "U podizanju"],
  ok: ["green", "Dobro"], oslabljeno: ["orange", "Oslabljeno"], nedostaje: ["gray", "Nedostaje"], blagi_stres: ["yellow", "Blagi stres"],
  izrazeni_stres: ["orange", "Izražen stres"], kriticno: ["red", "Kritično"], ready: ["green", "Obrađeno"], uploaded: ["gray", "Učitano"],
  processing: ["blue", "Obrada"], na_cekanju: ["yellow", "Na čekanju"], odobren: ["green", "Odobren"], odbijen: ["red", "Odbijen"],
  provjera: ["blue", "Provjera"], uvezeno: ["green", "Uvezeno"], djelimicno: ["orange", "Djelimično"], odbijeno: ["red", "Odbijeno"],
  greska: ["red", "Greška"], upozorenje: ["yellow", "Upozorenje"],
};

export function StatusBadge({ status }: { status?: string | null }) {
  const { t } = useT();
  if (!status) return null;
  const [c, l] = STATUS[status] ?? ["gray", status];
  return <Badge color={c} variant="light" size="sm">{t(l)}</Badge>;
}

export function Empty({ text = "Nema podataka" }: { text?: string }) {
  const { t } = useT();
  return <Text c="dimmed" size="sm" ta="center" py="lg">{t(text)}</Text>;
}

export const SEASONS = (() => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2, y - 3].map(String);
})();


/** Srpska množina: plural(n, "predmet", "predmeta", "predmeta") → 1 predmet, 2 predmeta, 5 predmeta. */
export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}
